import { useEffect, useState } from "react";
import ElementEnum from "../../enums/ElementEnum";
import PermissionLevelEnum from "../../enums/PermissionLevelEnum";
import ItemCliente from "../../models/ItemCliente";
import ItemPermiso from "../../models/ItemPermiso";
import ItemUsuario from "../../models/ItemUsuario";
import ApiService from "../../services/api/api.service";
import IAuthStatus from "./IAuth";

const getSessionStorageOrDefault = (key: string, defaultValue: any, asString: boolean) : any => {
    const stored = sessionStorage.getItem(key);
    if (!stored) {
        return defaultValue;
    }
    else {
        if (asString) {
            return stored;
        }
        else {
            try {
                return JSON.parse(stored);
            }
            catch {
                return defaultValue;
            }
        }
    }
};

const getDefaultPermission = (elementId: ElementEnum) : ItemPermiso => {
    let permission = new ItemPermiso();
    permission.ControlTotal = false;
    permission.Elemento = "";
    permission.Eliminar = false;
    permission.IdElemento = elementId;
    permission.Modificar = false;
    permission.Nuevo = false;
    permission.Ver = false;
    return permission;
};

export default function useAuthStatus() : IAuthStatus {
    const [user, setUser] = useState<ItemUsuario | null>(getSessionStorageOrDefault('user', null, false));

    useEffect(() => {
        if (user) {
            sessionStorage.setItem('user', JSON.stringify(user));
        }
        else {
            sessionStorage.removeItem('user');
        }
    }, [user]);

    return {
        isLoggedIn: (user !== null),
        user: user || undefined,
        setUser: (itemUsuario: ItemUsuario) : void => {
            setUser(itemUsuario);
        },
        getPermission: (elementId: ElementEnum) : ItemPermiso => {
            if (user === null) {
                return getDefaultPermission(elementId);
            }
            else {
                return user.Permisos.find((permission: ItemPermiso) => {
                    return (permission.IdElemento === elementId);
                }) || getDefaultPermission(elementId);
            }
        },
        hasPermission: (elementId: ElementEnum, permissionLevel: PermissionLevelEnum) : boolean => {
            if (user === null) {
                return false;
            }
            else {
                const permission : ItemPermiso = user.Permisos.find((permission: ItemPermiso) => {
                    return (permission.IdElemento === elementId);
                }) || getDefaultPermission(elementId);
                if (permissionLevel === PermissionLevelEnum.TOTAL_CONTROL) {
                    return permission.ControlTotal;
                }
                else if (permissionLevel === PermissionLevelEnum.VIEW) {
                    return permission.Ver;
                }
                else if (permissionLevel === PermissionLevelEnum.CREATE) {
                    return permission.Nuevo;
                }
                else if (permissionLevel === PermissionLevelEnum.UPDATE) {
                    return permission.Modificar;
                }
                else if (permissionLevel === PermissionLevelEnum.DELETE) {
                    return permission.Eliminar;
                }
                else {
                    return false;
                }
            }
        },
        login: (itemUsuario: ItemUsuario) : Promise<ItemCliente[]> => {
            return new Promise<ItemCliente[]>((resolve, reject) => {
                ApiService.Login(itemUsuario.IdUsuario, itemUsuario.Clave).then((customers: ItemCliente[]) => {
                    resolve(customers);
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        selectCustomer: (itemUsuario: ItemUsuario) : Promise<ItemUsuario> => {
            return new Promise<ItemUsuario>((resolve, reject) => {
                ApiService.SelectCustomer(itemUsuario.IdUsuario, itemUsuario.Clave, itemUsuario.CodigoCliente).then((user: ItemUsuario) => {
                    user.CodigoCliente = itemUsuario.CodigoCliente;
                    ApiService.Post<ItemPermiso[]>("Usuarios", "GetPermisos", user, {
                        "Usuario": user.IdUsuario,
                    }).then((permissions: ItemPermiso[]) => {
                        user.Permisos = permissions;
                        resolve(user);
                    }, (error: string) => {
                        reject(error);
                    })
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        changeSpace: (itemUsuario: ItemUsuario,  codigoCliente: string) : Promise<ItemUsuario> => {
            return new Promise<ItemUsuario>((resolve, reject) => {
                ApiService.Post<ItemUsuario>("Usuarios", "CambiarEspacio", itemUsuario, {
                    "CodigoCliente": codigoCliente,
                }).then((user: ItemUsuario) => {
                    user.CodigoCliente = codigoCliente;
                    ApiService.Post<ItemPermiso[]>("Usuarios", "GetPermisos", user, {
                        "Usuario": user.IdUsuario,
                    }).then((permissions: ItemPermiso[]) => {
                        user.Permisos = permissions;
                        resolve(user);
                    }, (error: string) => {
                        reject(error);
                    })
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        register: (itemCliente: ItemCliente) : Promise<string> => {
            return new Promise<string>((resolve, reject) => {
                ApiService.Register(itemCliente.Email, itemCliente.P1, itemCliente.Nombre, itemCliente.Espacio, itemCliente.Prefijo, itemCliente.Telefono).then((response: string) => {
                    resolve(response);
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        confirmRegister: (itemCliente: ItemCliente, token: string) : Promise<string> => {
            return new Promise<string>((resolve, reject) => {
                ApiService.ConfirmRegister(itemCliente.Email, itemCliente.P1, token).then((response: string) => {
                    resolve(response);
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        confirmRegisterInvitation: (itemCliente: ItemCliente, token: string) : Promise<string> => {
            return new Promise<string>((resolve, reject) => {
                ApiService.ConfirmRegisterInvitation(itemCliente.Email, itemCliente.P1, itemCliente.Nombre, token).then((response: string) => {
                    resolve(response);
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        recover: (itemCliente: ItemCliente) : Promise<string> => {
            return new Promise<string>((resolve, reject) => {
                ApiService.Recover(itemCliente.Email).then((response: string) => {
                    resolve(response);
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        confirmRecover: (itemCliente: ItemCliente, token: string) : Promise<string> => {
            return new Promise<string>((resolve, reject) => {
                ApiService.ConfirmRecover(itemCliente.Email, token).then((response: string) => {
                    resolve(response);
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        changePasswordRecover: (itemCliente: ItemCliente, token: string) : Promise<string> => {
            return new Promise<string>((resolve, reject) => {
                ApiService.ChangePasswordRecover(itemCliente.Email, itemCliente.P1, token).then((response: string) => {
                    resolve(response);
                }, (error: string) => {
                    reject(error);
                });
            });
        },
        logout: () : void => {
            setUser(null);
        },
    };
};
